import { Usb, AlertTriangle } from 'lucide-react';
import { spore } from '@/spore/runtime';
import { Panel } from './Panel';

const STATUS = {
  idle: { label: 'NOT CONNECTED', color: 'var(--muted)' },
  requesting: { label: 'REQUESTING ACCESS…', color: 'var(--amber)' },
  unsupported: { label: 'WEB MIDI UNSUPPORTED', color: 'var(--coral)' },
  'permission-denied': { label: 'PERMISSION DENIED', color: 'var(--coral)' },
  'no-device': { label: 'NO DEVICE', color: 'var(--amber)' },
  connected: { label: 'CONNECTED', color: 'var(--spore)' },
  error: { label: 'MIDI ERROR', color: 'var(--coral)' },
};

export const StatusBadge = ({ status }) => {
  const s = STATUS[status] || STATUS.idle;
  return (
    <span data-testid="biotron-status-badge" data-status={status} className="inline-flex items-center gap-2 border px-3 py-1 text-xs tracking-[0.2em]" style={{ borderColor: s.color, color: s.color }}>
      <span className={`dot-live ${status === 'connected' ? 'dot-pulse' : ''}`} style={{ background: s.color }} />
      {s.label}
    </span>
  );
};

const Hint = ({ status, info }) => {
  if (status === 'unsupported')
    return <p data-testid="midi-unsupported-message" className="text-lg glow-amber">Open this in Chrome or Edge on a desktop/laptop.</p>;
  if (status === 'permission-denied')
    return <p data-testid="midi-permission-message" className="text-sm text-[var(--coral)]">MIDI permission was blocked. Click the site-settings icon in the address bar, allow MIDI devices, then press Connect again.</p>;
  if (status === 'no-device')
    return <p data-testid="midi-no-device-message" className="text-sm text-[var(--amber)]">No MIDI input found{info.lastDeviceName ? ` — waiting for “${info.lastDeviceName}” to return` : ''}. Plug in the Biotron via USB; it will be picked up automatically.</p>;
  if (status === 'error') return <p className="text-sm text-[var(--coral)]">{info.error}</p>;
  return null;
};

export const DevicePanel = ({ info }) => {
  const { status } = info;
  return (
    <Panel testId="device-panel" code="01 / INPUT" title="Biotron Link" delay={0} right={<Usb size={18} className="text-[var(--teal)]" />}>
      <div className="space-y-4">
        <StatusBadge status={status} />
        <div>
          <div className="label-eyebrow mb-1">Device</div>
          <div data-testid="biotron-device-name" className="text-lg text-[var(--ink)]">{info.deviceName || '—'}</div>
        </div>
        <Hint status={status} info={info} />
        <button data-testid="biotron-connect-button" className="btn-lab primary w-full" disabled={!info.supported || status === 'requesting'} onClick={() => spore.midi.connect()}>
          {status === 'connected' ? 'Rescan Devices' : 'Connect Biotron'}
        </button>
        <div>
          <label className="label-eyebrow mb-1 block" htmlFor="midi-input">MIDI Input</label>
          <select id="midi-input" data-testid="midi-input-select" className="select-lab" disabled={!info.inputs.length} value={info.selectedId || ''} onChange={(e) => spore.midi.select(e.target.value)}>
            {!info.inputs.length && <option value="">No inputs</option>}
            {info.inputs.map((i) => (
              <option key={i.id} value={i.id}>{i.name}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs text-[var(--muted)]">
          <span data-testid="midi-disconnect-count">DISCONNECTS · {info.disconnects}</span>
          <span data-testid="midi-reconnect-count">RECONNECTS · {info.reconnects}</span>
        </div>
        {!window.isSecureContext && (
          <p className="flex items-center gap-2 text-xs text-[var(--coral)]"><AlertTriangle size={14} /> Web MIDI requires HTTPS.</p>
        )}
      </div>
    </Panel>
  );
};
